import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * Shared parts of ItemStatusSlice — plan §5.2. 10 identical reducers; web adds 1 plus the
 * logout `extraReducers`.
 *
 * ⚠️ `getSoldOutItems` is NOT here — it diverges in SIGNATURE, not body: web writes
 * `(state, action) => {}` and mobile `(state) => {}`. Both bodies are empty, so the runtime
 * behaviour is identical; what differs is the TYPE RTK infers for the generated creator —
 * mobile's rejects an argument at compile time, web's requires one to be allowed. Authored
 * per platform so `tsc` keeps enforcing each app's existing call sites.
 *
 * ⚠️ `action` must be `PayloadAction<any>`, never `any` — see the ledger. Reducers the apps
 * wrote with no `action` param (`emptyPrepareItemsResponse`, `emptyRefreshItemsResponse`)
 * keep that shape.
 *
 * `inStockItems` / `soldOutItems` are typed as objects but initialise to `[]` in both apps,
 * and `emptyPrepareItemsResponse` resets `prepareItemsResponse` to `[]` while
 * `emptyRefreshItemsResponse` resets its field to `{}`. All preserved verbatim.
 *
 * This module was previously listed as BLOCKED on `helper/catalogueHelpers`. It was not —
 * every catalogueHelpers import in both app files was DEAD (imported, never referenced). See
 * the session log.
 */
export interface SharedItemsState {
  inStockItems: {
    [key: string]: any;
  };
  soldOutItems: {
    [key: string]: any;
  };
  prepareItemsResponse: any;
  refreshItemsResponse: any;
  /**
   * The flat list of sold-out `itemId`s — the one field UI code should test membership against.
   *
   * 🔴 **Moved here from `index.desktop.ts` on 2026-08-29 (ledger hazard 38, error 1), together
   * with its reducer and a `put` in mobile's saga.** It was web-only, and mobile paid for that
   * twice: the kiosk's `soldOutItemIds` read `undefined`, and TWO LIVE mobile POS consumers
   * already read this exact field and silently got nothing —
   * `helper/catalogueHelpers.js:checkItemOutOfStock` (which feeds `itemsViewObj(...).isInActive`,
   * the item card's sold-out state) and `commonComponents/RecommendedItems`. Mobile was
   * internally inconsistent about it: `ItemsView/itemListViewCard` and `CustomiseItemPopupMb`
   * read `soldOutItems` instead and worked, because on mobile that field happens to hold the
   * flattened id list too.
   *
   * ⚠️ Which is why the compiler's "Did you mean `soldOutItems`?" must NOT be taken: the two
   * fields agree on MOBILE only. On web `soldOutItems` is the RAW category tree from
   * `getSoldOutItemsSuccess`, so a shared file reading it would break the web kiosk. Same action
   * type, different payload shape per platform — see `saga/itemStatus/index.native.ts`.
   */
  soldOutItemsList: string[];
}

export const sharedInitialState: SharedItemsState = {
  inStockItems: [],
  soldOutItems: [],
  prepareItemsResponse: {},
  refreshItemsResponse: {},
  soldOutItemsList: [],
};

/** Identical in both apps. */
export const sharedReducers = {
  getInStockItems: (state: SharedItemsState, action: PayloadAction<any>) => {},
  getInStockItemsSuccess: (state: SharedItemsState, action: PayloadAction<any>) => {
    state.inStockItems = action.payload;
  },
  getSoldOutItemsSuccess: (state: SharedItemsState, action: PayloadAction<any>) => {
    state.soldOutItems = action.payload;
  },
  prepareItems: (state: SharedItemsState, action: PayloadAction<any>) => {},
  prepareItemsSuccess: (state: SharedItemsState, action: PayloadAction<any>) => {
    state.prepareItemsResponse = action.payload;
  },
  refreshItems: (state: SharedItemsState, action: PayloadAction<any>) => {},
  refreshItemsSuccess: (state: SharedItemsState, action: PayloadAction<any>) => {
    state.refreshItemsResponse = action.payload;
  },
  emptyPrepareItemsResponse : (state: SharedItemsState) => {
    state.prepareItemsResponse = [];
  },
  emptyRefreshItemsResponse : (state: SharedItemsState) => {
      state.refreshItemsResponse = {};
  },
  /**
   * Store the flattened sold-out id list. Both platforms compute exactly this list in their
   * `getSoldOutItemsData` worker (`flatItemsFromCategories(response)`) behind the same
   * "show out of stock" gate — web off the action payload, mobile off business settings — so
   * this reducer was shareable the moment mobile dispatched it.
   *
   * The `.map` is a no-op on already-flat ids and is preserved from web verbatim: it also
   * accepts an object list, which is what makes the reducer safe for either caller.
   */
  getCurrentSoldOutItemSuccess: (state: SharedItemsState, action: PayloadAction<any>) => {
    let itemsList = action.payload;
    state.soldOutItemsList = itemsList.map((item: any) => item.itemId || item);
  },
};
